import { CheckIcon, TriangleAlertIcon, XIcon } from 'lucide-react-native';
import { useAnalytics } from '@/components/analytics-provider';
import * as React from 'react';
import { ActivityIndicator, View } from 'react-native';

import { BRAND } from '@sixam/config/brand';
import { MemberQrScanner } from '@/components/qr/member-qr-scanner';
import { Icon } from '@sixam/ui/icon';
import { Text } from '@sixam/ui/text';
import { checkInEventMember, InvalidMemberQrError } from '@/lib/event-checkins';
import type { ParsedMemberQrJwt } from '@/lib/member-qr';

type CheckInStationProps = Readonly<{
  eventId: string;
  /** Called after a runner is checked in, so a roster can refresh. */
  onCheckedIn?: () => void;
}>;

// How long a result stays up before it clears and the scanner re-arms for the
// next runner — long enough to read the name at arm's length. Also stops the
// same QR, still in frame, from being read on a loop.
const REARM_DELAY_MS = 3000;

/**
 * What the operator sees for a beat after each scan. Nothing here is a question
 * and nothing here is an action: the runner is already in, the card clears
 * itself, and the queue keeps moving. The camera is never covered by a dialog.
 */
export type ScanOutcome = Readonly<{
  detail: string | null;
  name: string | null;
  tone: 'success' | 'warning' | 'error';
}>;

/** Shown for a QR the server rejects — the operator needs a person, not a code. */
const UNKNOWN_QR_OUTCOME: ScanOutcome = {
  detail: 'Ask them to talk to AJ or Brandon.',
  name: "Person doesn't exist",
  tone: 'error',
};

const TONE_STYLES = {
  error: { background: BRAND.color.danger, icon: XIcon },
  success: { background: BRAND.color.success, icon: CheckIcon },
  warning: { background: BRAND.color.warning, icon: TriangleAlertIcon },
} as const;

export function ScanResultCard({ outcome }: Readonly<{ outcome: ScanOutcome }>) {
  const tone = TONE_STYLES[outcome.tone];

  return (
    <View
      className="items-center gap-3 rounded-md p-5"
      style={{ backgroundColor: tone.background }}
    >
      <View className="size-16 items-center justify-center rounded-full bg-white/20">
        <Icon as={tone.icon} className="size-10 text-white" />
      </View>
      {outcome.name ? (
        <Text className="text-center text-2xl font-bold text-white" numberOfLines={2}>
          {outcome.name}
        </Text>
      ) : null}
      {outcome.detail ? <Text className="text-center text-white/80">{outcome.detail}</Text> : null}
    </View>
  );
}

/**
 * The one check-in scanner used everywhere. Scan a runner and they are checked
 * in immediately — no confirmation step, because at a start line a dialog
 * between the camera and the queue costs more than the mis-scans it prevents.
 * The result flashes up, clears itself, and the next runner steps forward.
 */
export function CheckInStation({ eventId, onCheckedIn }: CheckInStationProps) {
  const posthog = useAnalytics();
  const [rearmToken, setRearmToken] = React.useState(0);
  const [isBusy, setIsBusy] = React.useState(false);
  const [outcome, setOutcome] = React.useState<ScanOutcome | null>(null);
  const mountedRef = React.useRef(true);
  const timerRef = React.useRef<ReturnType<typeof setTimeout> | undefined>(undefined);

  React.useEffect(() => {
    return () => {
      mountedRef.current = false;
      if (timerRef.current) {
        clearTimeout(timerRef.current);
      }
    };
  }, []);

  // Re-arming clears the result too, so the card is gone by the time the next
  // runner steps up rather than lingering until someone else is scanned.
  const rearm = React.useCallback(() => {
    if (!mountedRef.current) {
      return;
    }
    setOutcome(null);
    setIsBusy(false);
    setRearmToken((token) => token + 1);
  }, []);

  const settle = React.useCallback(
    (next: ScanOutcome) => {
      if (!mountedRef.current) {
        return;
      }
      setOutcome(next);
      timerRef.current = setTimeout(rearm, REARM_DELAY_MS);
    },
    [rearm]
  );

  const handleScanned = React.useCallback(
    (scan: ParsedMemberQrJwt) => {
      if (isBusy) {
        return;
      }
      setIsBusy(true);
      setOutcome(null);

      if (!scan.isValid) {
        settle(UNKNOWN_QR_OUTCOME);
        return;
      }

      checkInEventMember(eventId, scan.jwt)
        .then((response) => {
          onCheckedIn?.();
          if (response.status !== 'duplicate') {
            posthog.capture('event_check_in_completed', {
              needs_review: Boolean(response.needsReview),
            });
          }
          const name = response.member?.name ?? '6am member';
          const isPlainSuccess = response.status === 'checked_in' && !response.needsReview;
          settle({
            // The runner is in either way; a duplicate only tells the operator
            // they have already been scanned, so it reads as a caution.
            detail: isPlainSuccess ? null : response.message,
            name,
            tone: response.status === 'duplicate' || response.needsReview ? 'warning' : 'success',
          });
        })
        .catch((scanError: unknown) => {
          if (scanError instanceof InvalidMemberQrError) {
            settle(UNKNOWN_QR_OUTCOME);
            return;
          }
          settle({
            detail: scanError instanceof Error ? scanError.message : 'Could not check in runner.',
            name: null,
            tone: 'error',
          });
        });
    },
    [eventId, isBusy, onCheckedIn, posthog, settle]
  );

  return (
    <View className="flex-1">
      <MemberQrScanner active fill onScanned={handleScanned} rearmToken={rearmToken} />

      {/*
        The result sits over the bottom of the camera so the viewfinder itself is
        never covered — the operator can keep scanning while it is still up.
      */}
      <View className="absolute inset-x-0 bottom-0 gap-3 p-4" pointerEvents="box-none">
        {isBusy && !outcome ? <ActivityIndicator color="white" /> : null}
        {outcome ? <ScanResultCard outcome={outcome} /> : null}
      </View>
    </View>
  );
}
